<i18n
  src="@/locales/views/packing_house/quality_control/query_container/show_quality_inspect.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/quality_inspect.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_quality_inspects')"
    :params="{
      packing_house_id: packing_house_id,
      container_id: container_id,
      packing_house_process_id: packing_house_process_id,
      quality_template_id: quality_template_id
    }"
  ></go-back-link>
  <bread-crumbs
    :labels="[
      module_name,
      packing_house_process.name,
      container.container_code,
      quality_template?.name
    ]"
  ></bread-crumbs>
  <template v-if="load_general_data">
    <container-info :container="container" :show_info="true"></container-info>
    <quality-inspect-info-read-only
      :quality_inspect="quality_inspect"
    ></quality-inspect-info-read-only>
    <tabs-container v-if="option_tabs.length > 0" v-model="tabsProxy" />

    <div
      v-for="(option_tab, index) in option_tabs"
      :key="index"
      class="mt-6"
      v-show="option_tab.current"
    >
      <quality-group-details
        v-if="option_tab.current"
        :use_samplings="quality_template?.use_samplings"
        :quality_attributes="quality_attributes"
        :quality_samplings="quality_samplings"
        :quality_inspect="quality_inspect"
        :quality_group="current_group"
        :quality_template="quality_template"
      />
    </div>
  </template>
  <loading-message v-else />
</template>

<script>
import GoBackLink from "@/components/views/layout/GoBackLink.vue";
import QueryContainerRequester from "@/views/packing_house/requesters/quality_control/query_container/show_quality_inspect.js";
import QualityInspectInfoReadOnly from "@/components/views/packing_house/quality_control/query_container/QualityInspectInfo.vue";
import QualityGroupDetails from "@/components/views/packing_house/quality_control/query_container/show_inspect/QualityGroupDetails.vue";
import ContainerInfo from "@/components/views/packing_house/quality_control/query_container/ContainerInfo.vue";

export default {
  name: "PhQualityControlQueryContainerShowQualityInspect",
  components: {
    GoBackLink,
    ContainerInfo,
    QualityGroupDetails,
    QualityInspectInfoReadOnly
  },
  data() {
    return {
      load_general_data: false,
      quality_inspect: {},
      quality_template: {},
      packing_house_process: {},
      container: {},
      option_tabs: [],
      quality_groups: [],
      quality_sampling_details: []
    };
  },
  props: {
    packing_house_id: { required: true },
    container_id: {
      required: true
    },
    quality_inspect_id: {
      required: true
    },
    quality_template_id: {
      required: true
    },
    packing_house_process_id: {
      required: true
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id
        }
      }).then(() => {
        this.fetchData();
      })
    );
  },
  computed: {
    current_group: function () {
      return this.quality_groups.find(
        quality_group => quality_group.id == this.find_current_tab?.id
      );
    },
    find_current_tab() {
      return this.option_tabs.find(option_tab => option_tab.current);
    },
    quality_attributes: function () {
      return this.quality_groups
        .find(quality_group => quality_group.id == this.find_current_tab?.id)
        .quality_attributes.sort((a, b) => a.position - b.position);
    },
    quality_samplings: function () {
      let quality_samplings = [];
      for (
        let sampling = 1;
        sampling <= this.quality_inspect.samplings;
        sampling++
      ) {
        let quality_sampling = this.quality_inspect.quality_samplings.find(
          quality_sampling => quality_sampling.sampling == sampling
        );
        quality_samplings.push(quality_sampling || {});
      }
      return quality_samplings;
    },
    tabsProxy: {
      get() {
        return Array.isArray(this.option_tabs)
          ? this.option_tabs
          : Object.values(this.option_tabs);
      },
      set(newValue) {
        this.option_tabs = Array.isArray(newValue)
          ? newValue
          : Object.values(newValue);
      }
    },
    show_legend: function () {
      return (
        this.quality_attributes.map(qa => qa.abbreviation).filter(abbr => abbr)
          .length > 0
      );
    }
  },
  methods: {
    fetchData: async function () {
      await this.fetchPackingHouseProcess();
      await this.fetchContainer();
      await this.getQualityInspect().then(() => {
        this.fetchTemplate();
        this.fetchQualityGroups();
      });
      this.load_general_data = true;
    },
    fetchPackingHouseProcess: async function () {
      let response = await QueryContainerRequester.fetchPackingHouseProcess(
        this.packing_house_id,
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },
    fetchContainer: async function () {
      let response = await QueryContainerRequester.fetchContainer(
        this.packing_house_id,
        this.packing_house_process_id,
        this.container_id
      );
      this.container = response.data;
    },
    getQualityInspect: async function () {
      let response = await QueryContainerRequester.getQualityInspect(
        this.packing_house_id,
        this.quality_inspect_id
      );
      this.quality_inspect = response.data;
    },
    fetchTemplate: async function () {
      let response = await QueryContainerRequester.fetchTemplate(
        this.packing_house_id,
        this.container_id,
        this.quality_inspect.quality_template_id
      );
      this.quality_template = response.data;
    },
    listExportQualityGroups: function () {
      this.quality_groups = this.quality_inspect.quality_samplings
        .map(quality_sampling => quality_sampling.quality_sampling_groups)
        .flat()
        .map(quality_sampling_group => quality_sampling_group.quality_group)
        .reduce(
          (quality_groups, quality_group) =>
            quality_groups.find(item => item.id === quality_group.id)
              ? quality_groups
              : [...quality_groups, quality_group],
          []
        );
    },
    findExportQualitySamplingDetailValue: function (quality_attribute_id) {
      let quality_sampling_detail = this.quality_sampling_details.find(
        quality_sampling_detail =>
          quality_sampling_detail.quality_attribute_id === quality_attribute_id
      );

      return (
        quality_sampling_detail?.value ||
        quality_sampling_detail.value_float ||
        quality_sampling_detail.value_bool
      );
    },
    fetchQualityGroups: async function () {
      let response = await QueryContainerRequester.fetchQualityGroups(
        this.packing_house_id,
        this.quality_inspect.quality_template_id
      );
      this.quality_groups = response.data;
      this.option_tabs = this.quality_groups.map(eq_group => {
        return {
          id: eq_group.id,
          name: eq_group.name,
          icon: "fas fa-eye",
          current: this.quality_groups[0].id === eq_group.id
        };
      });
    },
    cellValue: function (eq_sampling, eq_attribute_id) {
      if (Object.keys(eq_sampling).length === 0) return;

      return eq_sampling.quality_sampling_groups
        .find(
          sampling_group =>
            sampling_group.quality_group_id == this.find_current_tab.id
        )
        ?.quality_sampling_details?.find(
          sampling_detail =>
            sampling_detail.quality_attribute_id === eq_attribute_id
        )?.value;
    }
  }
};
</script>
